import { useEffect } from 'react';

/**
 * Loads a stylesheet for as long as the component is mounted, then takes it away again.
 *
 * **This exists for one font, and the removal is the point.** `policy.html` is the only live page
 * that loads Playfair Display; `index.html` and `book.html` name it in their CSS and never fetch it,
 * so their serif headings fall back — which is what the landing-page port deliberately reproduces.
 * A single-page app has one document head, so loading it globally would quietly restyle the landing
 * page's headings the moment this page shipped. Mounted with the legal page and removed with it,
 * each page keeps the type the live site gives it.
 *
 * A `<link>` already in the head for the same `href` is reused and left alone — two mounts of the
 * same page must not queue a second copy, and the first must not remove one the second still wants.
 */
export function usePageStylesheet(href: string): void {
    useEffect(() => {
        const existing = document.head.querySelector(`link[data-page-stylesheet="${CSS.escape(href)}"]`);

        if (existing !== null) {
            return;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.dataset['pageStylesheet'] = href;
        document.head.appendChild(link);

        return () => link.remove();
    }, [href]);
}
